import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CognitoHostedUIIdentityProvider, Auth } from '@aws-amplify/auth';
import { SignOutOpts } from '@aws-amplify/auth/lib/types/Auth';
import { Hub } from '@aws-amplify/core';
import { NbToastrService } from '@nebular/theme';
import { DEFAULT_INTERRUPTSOURCES, Idle, LocalStorageExpiry } from '@ng-idle/core';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, from, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DEFAULT_SESSION_TIMEOUT, DEFAULT_SESSION_TIMEOUT_WARNING } from '../constants/session';

export interface AuthState {
  email?: string;
  phone?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  countdown?: number;
  error?: any;
  countdownStarted?: boolean;
}

const initialSessionState: AuthState = {
  countdown: null,
  error: null,
};

export interface IGetUserAttributes {
  email: string;
  // roles: Role[];
  department: string;
  office: string;
  termsAccepted: {
    date: string;
    version: string;
  } | null;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  state$: Observable<AuthState>;
  private _state$: BehaviorSubject<AuthState>;
  public loggedIn: boolean;
  private _authState: Subject<CognitoUser | any> = new Subject<CognitoUser | any>();
  authState: Observable<CognitoUser | any> = this._authState.asObservable();
  roles: string[] = [];
  routerEvents: Subscription;
  idleServiceInitialized: boolean = false;

  constructor(
    private logger: NGXLogger,
    private idle: Idle,
    private localStorageExp: LocalStorageExpiry,
    private toastrService: NbToastrService,
    private router: Router,
  ) {
    this._state$ = new BehaviorSubject(initialSessionState);
    this.state$ = this._state$.asObservable();
    Hub.listen('auth', data => {
      // this.logger.debug('auth data: ', data);
      const { channel, payload } = data;
      if (channel === 'auth') {
        this._authState.next(payload.event);
      }
    });
    this.initIdleServiceListener();
  }

  private setState(nextState: AuthState): void {
    const prevState = this.state;
    this._state$.next({ ...prevState, ...nextState });
  }

  get state(): AuthState {
    return this._state$.getValue();
  }

  public clearState(): void {
    this._state$.next(initialSessionState);
  }

  public setError(error: any): void {
    this.setState({ error });
  }

  reset() {
    this.setState(initialSessionState);
  }

  async signOut(opts?: SignOutOpts): Promise<any> {
    return Auth.signOut(opts);
  }

  openHostedUI(customState?: string) {
    return Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Cognito, customState });
  }

  async currentAuthenticatedUser() {
    const user = await Auth.currentAuthenticatedUser();
    this.setState({ email: user.attributes.email });

    return user;
  }

  async currentUserOfflineNotifications() {
    const user = await this.currentAuthenticatedUser();
    return user.attributes?.[`custom:notification`]
      ? JSON.parse(user.attributes?.[`custom:notification`])
      : new Object();
  }

  public isAuthenticated(): Observable<boolean> {
    return from(Auth.currentAuthenticatedUser()).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  public getUserRoles(): Observable<string[]> {
    return from(Auth.currentSession()).pipe(
      map(session => {
        this.roles = JSON.parse(session.getIdToken().payload['custom:roles']) || [];
        return this.roles;
      }),
      catchError(() => of([])),
    );
  }

  public hasRole(role: string): Observable<boolean> {
    return this.getUserRoles().pipe(
      map(userRoles => {
        return userRoles.indexOf(role) > -1;
      }),
    );
  }

  public hasAnyRole(roles: string[]): Observable<boolean> {
    return this.getUserRoles().pipe(
      map(userRoles => {
        return userRoles.some(role => roles.indexOf(role) > -1);
      }),
    );
  }

  public initIdleService(options?: { timeoutDuration?: number; timeoutWarningDuration?: number }) {
    if (this.idleServiceInitialized) {
      this.logger.warn('Idle service already initialized');
      return;
    }

    this.idleServiceInitialized = true;
    const { timeoutDuration, timeoutWarningDuration } = options || {};
    let sessionTimeout = timeoutDuration || DEFAULT_SESSION_TIMEOUT;

    const sessionTimeoutWarningPeriod = timeoutWarningDuration || DEFAULT_SESSION_TIMEOUT_WARNING;
    // ensuring that the session timeout values are valid to prevent errors while setting the idle value
    if (sessionTimeoutWarningPeriod >= sessionTimeout) {
      throw new Error('sessionTimeoutWarningPeriod cannot be equal or higher than sessionTimeout');
    }

    this.idle.setIdle(sessionTimeout - sessionTimeoutWarningPeriod);
    this.idle.setTimeout(sessionTimeoutWarningPeriod);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.startIdleService();

    this.logger.debug(
      `SessionService loaded with session timeout: ${sessionTimeout}s and warning period: ${sessionTimeoutWarningPeriod}s`,
    );
  }

  initIdleServiceListener() {
    const signOut = () => this.signOut().catch(err => this.logger.warn(err));
    if (this.localStorageExp.isExpired()) {
      this.destroySession();
      signOut();
    }
    this.idle.onIdleStart.subscribe(() => this.logger.warn('User gone idle!'));
    this.idle.onIdleEnd.subscribe(() => {
      this.logger.info('User no longer idle.');
      if (this.state.countdownStarted) {
        this.setState({ countdownStarted: false });
      }
    });
    this.idle.onTimeout.subscribe(() => {
      this.logger.warn('User session timed out!');
      this.destroySession();
      signOut();
    });
    this.idle.onTimeoutWarning.subscribe((countdown: number) => {
      if (countdown === this.idle.getTimeout()) {
        this.setState({ countdownStarted: true, countdown });
        this.toastrService.show(
          `Your session's been inactive for a while, signing out in ${countdown} second(s).`,
          undefined,
          { icon: null, duration: 50 },
        );
      }
    });
  }

  protected startIdleService(): void {
    this.idle.watch();
  }

  protected stopIdleService(): void {
    this.idleServiceInitialized = false;
    this.idle.stop();
    this.idle.clearInterrupts();
  }

  protected destroySession(): void {
    this.stopIdleService();
    this.localStorageExp.idling(true);
  }

}
